import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import axiosInstance from "utils/services/api";
import ReactLoading from "react-loading";
import { BsInfoCircle } from "react-icons/bs";
import { AiOutlineClose } from "react-icons/ai";

const broadcastValidationSchema = Yup.object().shape({
  role: Yup.string().required("Role is required"),
  title: Yup.string()
    .required("Title is required")
    .trim()
    .max(200, "Title must not exceed 200 characters"),
  description: Yup.string()
    .required("Description is required")
    .trim()
    .max(1000, "Description must not exceed 1000 characters"),
});

const specificUserValidationSchema = Yup.object().shape({
  userIds: Yup.array()
    .min(1, "Please select at least one user")
    .required("Please select at least one user"),
  title: Yup.string()
    .required("Title is required")
    .trim()
    .max(200, "Title must not exceed 200 characters"),
  description: Yup.string()
    .required("Description is required")
    .trim()
    .max(1000, "Description must not exceed 1000 characters"),
});

const Notifications = () => {
  const [activeTab, setActiveTab] = useState("broadcast");
  const [roleOptions, setRoleOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [isSubmittingBroadcast, setIsSubmittingBroadcast] = useState(false);
  const [isSubmittingSpecific, setIsSubmittingSpecific] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const searchRef = useRef(null);

  const broadcastInitialValues = {
    role: "",
    title: "",
    description: "",
  };

  const specificUserInitialValues = {
    userIds: [],
    title: "",
    description: "",
  };

  useEffect(() => {
    fetchRoleOptions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchRoleOptions = async () => {
    setLoadingRoles(true);
    try {
      const response = await axiosInstance.get("/roles");
      const data = response?.data?.data;
      if (data) {
        const filteredRoles = data.filter(role => role.name !== "Admin" && role.name !== "unknown user");
        setRoleOptions(filteredRoles);
      }
    } catch (error) {
      toast.error("Error fetching role options");
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleUserSearch = async (text) => {
    setSearchTerm(text);
    if (text.trim().length === 0) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axiosInstance.get(
        `/search/users?page=1&searchTerm=${encodeURIComponent(text)}`
      );
      const users = response?.data?.data?.users || [];
      setSearchResults(users);
      setShowSearchResults(true);
    } catch (error) {
      toast.error("Error searching users");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleUserSelect = (user, setFieldValue) => {
    const userId = user.id || user._id;
    if (!selectedUsers.some(u => (u.id || u._id) === userId)) {
      const newSelectedUsers = [...selectedUsers, user];
      setSelectedUsers(newSelectedUsers);
      if (setFieldValue) {
        setFieldValue("userIds", newSelectedUsers.map(u => String(u.id || u._id)));
      }
    }
    setSearchTerm("");
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const handleBroadcastSubmit = async (values) => {
    setIsSubmittingBroadcast(true);
    try {
      const response = await axiosInstance.post("/notification/by-role", {
        roles: [values.role],
        title: values.title.trim(),
        description: values.description.trim(),
      });
      toast.success(response?.data?.message || "Notification sent successfully");
      return true;
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to send notification"
      );
      return false;
    } finally {
      setIsSubmittingBroadcast(false);
    }
  };

  const handleSpecificUserSubmit = async (values) => {
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user");
      return false;
    }

    setIsSubmittingSpecific(true);
    try {
      // Convert userIds to strings array as required: ["1", "2", "3", ...]
      const userIds = selectedUsers.map(user => String(user.id || user._id));

      const response = await axiosInstance.post("/notification/to-user", {
        userIds: userIds,
        title: values.title.trim(),
        description: values.description.trim(),
      });

      toast.success(response?.data?.message || `Notification sent successfully to ${selectedUsers.length} user(s)`);
      setSelectedUsers([]);
      setSearchTerm("");
      return true;
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to send notification"
      );
      return false;
    } finally {
      setIsSubmittingSpecific(false);
    }
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-center">
        <div className="w-full bg-white p-4 shadow-lg">
          {/* Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-2 sm:space-x-4 md:space-x-8 overflow-x-auto">
              <button
                onClick={() => {
                  setActiveTab("broadcast");
                  setSelectedUsers([]);
                  setSearchTerm("");
                }}
                className={`whitespace-nowrap border-b-2 py-2 sm:py-3 md:py-4 px-2 sm:px-1 text-xs sm:text-sm font-medium flex-shrink-0 ${activeTab === "broadcast"
                  ? "border-[#4318FF] text-[#4318FF]"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }`}
              >
                Broadcast Notifications
              </button>
              <button
                onClick={() => {
                  setActiveTab("specific");
                  setSelectedUsers([]);
                  setSearchTerm("");
                }}
                className={`whitespace-nowrap border-b-2 py-2 sm:py-3 md:py-4 px-2 sm:px-1 text-xs sm:text-sm font-medium flex-shrink-0 ${activeTab === "specific"
                  ? "border-[#4318FF] text-[#4318FF]"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }`}
              >
                Notification to a Specific User
              </button>
            </nav>
          </div>

          {/* Broadcast Notifications Tab */}
          {activeTab === "broadcast" && (
            <div>
              <div
                className="rounded-b border-t-4 border-[#99C2D8] bg-[#99c2d82f] px-4 py-3 text-teal-900 shadow-sm"
                role="alert"
              >
                <div className="flex items-center">
                  <div className="px-2">
                    <BsInfoCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-bold">Broadcast Notification Details</p>
                  </div>
                </div>
              </div>

              <Formik
                initialValues={broadcastInitialValues}
                validationSchema={broadcastValidationSchema}
                onSubmit={async (values, { resetForm }) => {
                  const success = await handleBroadcastSubmit(values);
                  if (success) {
                    resetForm();
                  }
                }}
              >
                {({ values, setFieldValue }) => (
                  <Form className="flex flex-col px-5">
                    <div className="grid grid-cols-1 gap-4 p-4">
                      <div className="mb-2">
                        <label className="fw-bold block text-gray-700">
                          Select Role: <span className="text-red-500">*</span>
                        </label>
                        {loadingRoles ? (
                          <div className="flex items-center justify-center py-4">
                            <ReactLoading
                              type={"spin"}
                              color={"#4318FF"}
                              height={24}
                              width={24}
                            />
                          </div>
                        ) : (
                          <Field
                            as="select"
                            name="role"
                            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-[#4318FF] focus:ring-[#4318FF] sm:text-sm"
                          >
                            <option value="">Select Role</option>
                            <option value="All">All</option>
                            {roleOptions.map((role) => (
                              <option key={role.id || role._id} value={role.name}>
                                {role.name}
                              </option>
                            ))}
                          </Field>
                        )}
                        <ErrorMessage
                          name="role"
                          component="div"
                          className="text-red-500"
                        />
                      </div>

                      <div className="mb-2">
                        <label className="fw-bold block text-gray-700">
                          Title: <span className="text-red-500">*</span>
                        </label>
                        <Field
                          type="text"
                          name="title"
                          className="w-full rounded-md border px-3 py-2"
                          placeholder="Enter notification title"
                          maxLength={200}
                        />
                        <ErrorMessage
                          name="title"
                          component="div"
                          className="text-red-500"
                        />
                      </div>

                      <div className="mb-2">
                        <label className="fw-bold block text-gray-700">
                          Description: <span className="text-red-500">*</span>
                        </label>
                        <Field
                          as="textarea"
                          name="description"
                          rows={5}
                          className="w-full rounded-md border px-3 py-2"
                          placeholder="Enter notification description"
                          maxLength={1000}
                        />
                        <ErrorMessage
                          name="description"
                          component="div"
                          className="text-red-500"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        className="bg-[#4318FF] cursor-pointer rounded-md px-5 py-2 text-white hover:bg-blue-700"
                        disabled={isSubmittingBroadcast}
                      >
                        {isSubmittingBroadcast ? "Sending..." : "Send Notification"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}

          {/* Specific User Notification Tab */}
          {activeTab === "specific" && (
            <div>
              <div
                className="rounded-b border-t-4 border-[#99C2D8] bg-[#99c2d82f] px-4 py-3 text-teal-900 shadow-sm"
                role="alert"
              >
                <div className="flex items-center">
                  <div className="px-2">
                    <BsInfoCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-bold">Send Notification to Specific User</p>
                  </div>
                </div>
              </div>

              <Formik
                initialValues={specificUserInitialValues}
                validationSchema={specificUserValidationSchema}
                onSubmit={async (values, { resetForm }) => {
                  const success = await handleSpecificUserSubmit(values);
                  if (success) {
                    resetForm();
                  }
                }}
                enableReinitialize
              >
                {({ setFieldValue }) => (
                  <Form className="flex flex-col px-5">
                    <div className="grid grid-cols-1 gap-4 p-4">
                      <div className="mb-2">
                        <label className="fw-bold block text-gray-700">
                          Search User: <span className="text-red-500">*</span>
                        </label>
                        {selectedUsers.length > 0 && (
                          <div className="mb-2 rounded-md border border-green-200 bg-green-50 p-2">
                            <div className="text-sm font-semibold text-green-800 mb-2">
                              Selected Users ({selectedUsers.length}):
                            </div>
                            <div className={`flex flex-wrap gap-2 ${selectedUsers.length > 10 ? 'max-h-60 overflow-y-auto' : ''}`}>
                              {selectedUsers.map((user) => {
                                const userId = user.id || user._id;
                                return (
                                  <div
                                    key={userId}
                                    className="flex items-center justify-between rounded-md bg-white px-3 py-1.5 border border-green-300"
                                  >
                                    <div className="text-sm text-green-800 pr-2">
                                      {user.name || "No Name"} ({user.email || "No Email"})
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedUsers = selectedUsers.filter(u => (u.id || u._id) !== userId);
                                        setSelectedUsers(updatedUsers);
                                        setFieldValue("userIds", updatedUsers.map(u => String(u.id || u._id)));
                                      }}
                                      className="ml-1 text-red-500 hover:text-red-700 flex-shrink-0"
                                      aria-label="Remove selected user"
                                    >
                                      <AiOutlineClose className="h-4 w-4" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div className="relative" ref={searchRef}>
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => handleUserSearch(e.target.value)}
                            onFocus={() => {
                              // If user has selected users, clear the search term when clicking on search field
                              if (selectedUsers.length > 0) {
                                setSearchTerm("");
                                setSearchResults([]);
                              }
                              // If there are search results or text exists, show results
                              if (searchResults.length > 0 || searchTerm.trim().length > 0) {
                                setShowSearchResults(true);
                              }
                            }}
                            className="w-full rounded-md border px-3 py-2"
                            placeholder="Search by name or email"
                          />
                          {isSearching && (
                            <div className="absolute right-3 top-2.5">
                              <ReactLoading
                                type={"spin"}
                                color={"#4318FF"}
                                height={20}
                                width={20}
                              />
                            </div>
                          )}
                          {showSearchResults && searchResults.length > 0 && (
                            <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg max-h-60 overflow-y-auto">
                              {searchResults.map((user) => {
                                const userId = user.id || user._id;
                                const isSelected = selectedUsers.some(u => (u.id || u._id) === userId);
                                return (
                                  <div
                                    key={userId}
                                    onClick={() => {
                                      if (!isSelected) {
                                        handleUserSelect(user, setFieldValue);
                                      }
                                    }}
                                    className={`cursor-pointer px-4 py-2 border-b border-gray-200 last:border-b-0 ${
                                      isSelected 
                                        ? 'bg-gray-100 opacity-60 cursor-not-allowed' 
                                        : 'hover:bg-gray-100'
                                    }`}
                                  >
                                    <div className="font-medium">
                                      {user.name || "No Name"}
                                      {isSelected && <span className="ml-2 text-xs text-green-600">(Selected)</span>}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {user.email || ""}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {showSearchResults &&
                            searchResults.length === 0 &&
                            searchTerm.trim().length > 0 &&
                            !isSearching && (
                              <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg px-4 py-2">
                                <div className="text-gray-500">No users found</div>
                              </div>
                            )}
                        </div>
                        <Field
                          type="hidden"
                          name="userIds"
                          value={selectedUsers.map(u => String(u.id || u._id))}
                        />
                        <ErrorMessage
                          name="userIds"
                          component="div"
                          className="text-red-500"
                        />
                      </div>

                      <div className="mb-2">
                        <label className="fw-bold block text-gray-700">
                          Title: <span className="text-red-500">*</span>
                        </label>
                        <Field
                          type="text"
                          name="title"
                          className="w-full rounded-md border px-3 py-2"
                          placeholder="Enter notification title"
                          maxLength={200}
                        />
                        <ErrorMessage
                          name="title"
                          component="div"
                          className="text-red-500"
                        />
                      </div>

                      <div className="mb-2">
                        <label className="fw-bold block text-gray-700">
                          Description: <span className="text-red-500">*</span>
                        </label>
                        <Field
                          as="textarea"
                          name="description"
                          rows={5}
                          className="w-full rounded-md border px-3 py-2"
                          placeholder="Enter notification description"
                          maxLength={1000}
                        />
                        <ErrorMessage
                          name="description"
                          component="div"
                          className="text-red-500"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        className="bg-[#4318FF] cursor-pointer rounded-md px-5 py-2 text-white hover:bg-blue-700"
                        disabled={isSubmittingSpecific}
                      >
                        {isSubmittingSpecific ? "Sending..." : "Send Notification"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;